/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../../visualizations/renderer/VisTooltipInfo',
	'../../../lib/@waca/core-client/js/core-client/utils/BrowserUtils'
], function(VisTooltipInfo, BrowserUtils) {

	class MobileEventMapProvider  {
		constructor({ content, features }) {
			this.features = features;
			this.content = content;
			this.features['EventMap'].registerEvents(this._getEventMapList());
		}

		initialize() {
			this._eventMap = null;
			this._visGesture = null;
			this._api = null;
			return Promise.resolve();
		}

		_getVisualizationGesture(){
			//We don't cache feature here, because it is cached at feature loader level; 
			//If we cache, it could go stale when switching vis type 
			return this.content.getFeature('VisualizationGesture.internal');
		}

		isMobile(){
			return BrowserUtils.isMobile();
		}

		_getEventMapList(){
			if (!this._eventMap){
				this._eventMap = [];
				const hoverEvents = [{
					'gesture': this.features['EventMap'].GESTURES.HOVER,
					'key': 'mousemove touchmove',
					'override': true
				}, {
					'gesture': this.features['EventMap'].GESTURES.HOVER_END,
					'key': 'mouseleave touchend',
					'override': true
				}];

				const panAndZoomEvents = [{
					'gesture':  this.features['EventMap'].GESTURES.MOUSE_DOWN,
					'key': 'mousedown',
					//todo: could remove mouse event handling in mobile devices;
					'callback': this.onPanAndZoomEvents.bind(this,'onMouseDown'),
					'override': true
				}, {
					'gesture':  this.features['EventMap'].GESTURES.MOUSE_UP,
					'key': 'mouseup',
					//todo: could remove mouse event handling in mobile devices;
					'callback': this.onPanAndZoomEvents.bind(this,'onMouseUp'),
					'override': true
				}, {
					'gesture': this.features['EventMap'].GESTURES.WHEEL,
					'key': 'mousewheel wheel',
					//todo: could remove mouse event handling in mobile devices;
					'callback': this.onPanAndZoomEvents.bind(this,'onMouseWheel'),
					'override': true
				}, {
					'gesture': this.features['EventMap'].GESTURES.PAN_START,
					'key': 'touchstart',
					'callback': this.onPanAndZoomEvents.bind(this, 'onClickTapPanStart'),
					'override': true
				}, {
					'gesture': this.features['EventMap'].GESTURES.PAN_END,
					'key': 'touchend',
					'callback': this.onPanAndZoomEvents.bind(this,'onPanEnd'),
					'override': true
				}, {
					'gesture': this.features['EventMap'].GESTURES.PAN_MOVE,
					'key': 'touchmove',
					'callback': this.onPanAndZoomEvents.bind(this,'onPanMove'),
					'override': true
				}, {
					'gesture': this.features['EventMap'].GESTURES.ZOOM_START,
					'key': 'transformstart',
					'callback': this.onPanAndZoomEvents.bind(this,'onPinchStart'),
					'hammer': true,
					'override': true
				}, {
					'gesture': this.features['EventMap'].GESTURES.ZOOM,
					'key': 'transform',
					'callback': this.onPanAndZoomEvents.bind(this, 'onPinch'),
					'hammer': true,
					'override': true
				}, {
					'gesture': this.features['EventMap'].GESTURES.ZOOM_END,
					'key': 'transformend',
					'callback': this.onPanAndZoomEvents.bind(this, 'onPinchEnd'),
					'hammer': true,
					'override': true
				},{
					'gesture': this.features['EventMap'].GESTURES.HOVER_END,
					'key': 'mouseleave touchend',
					'callback': this.onMouseLeave.bind(this),
					'override': true
				}];

				//todo: onMouseLeave handling;
				const tapAndHoldEvents = [
					{
						'gesture':  this.features['EventMap'].GESTURES.SELECT,
						'key':'clicktap', // remove the clickta -- we are using tap instead
						'override': true
					},
					{
						'gesture':  this.features['EventMap'].GESTURES.MENU,
						'key':'contextmenu',
						'override': true
					},
					{
						'gesture':  this.features['EventMap'].GESTURES.SELECT,
						'key': 'tap',
						'hammer': true,
						'callback': (event) => this.onTap(event),
						'override': true
					}, {
						'gesture': this.features['EventMap'].GESTURES.MULTI_SELECT,
						'key': 'hold',
						'callback': (event) => this.onHold(event),
						'hammer': true,
						'override': true
					}];
				this._eventMap.push( ...tapAndHoldEvents, ...panAndZoomEvents, ...hoverEvents);

			}
			return this.isMobile() ? this._eventMap: [];
		}

		onMouseLeave(event){
			try {
				return this._getVisualizationGesture().onMouseLeave(event);
			}catch (e){
				//ignore
			}
		}

		onPanAndZoomEvents(handlerName, event){
			const isMaximized = (this.content.getFeature('WidgetAPI.deprecated') && this.content.getFeature('WidgetAPI.deprecated').isWidgetMaximized());
			if (isMaximized /* (enablePanAndZoomFor.indexOf(type) !== -1 /*&& canvas.getSelectedContentList().indexOf(this.content) !== -1)*/) {
				const eventHandlers = {
					'onMouseDown': 			(event) => this._getVisualizationGesture().onMouseDown(event),
					'onMouseUp': 			(event) => this._getVisualizationGesture().onMouseUp(event),
					'onMouseWheel': 		(event) => this._getVisualizationGesture().onMouseWheel(event),
					'onClickTapPanStart': 	(event) => this._getVisualizationGesture().onClickTapPanStart(event),
					'onPanEnd':				(event) => this._getVisualizationGesture().onPanEnd(event),
					'onPanMove':			(event) => this._getVisualizationGesture().onPanMove(event),
					'onPinchStart':			(event) => this._getVisualizationGesture().onPinchStart(event),
					'onPinch':				(event) => this._getVisualizationGesture().onPinch(event),
					'onPinchEnd':			(event) => this._getVisualizationGesture().onPinchEnd(event)
				};
				return eventHandlers[handlerName](event);
			}


		}

		onHold(){

		}

		showActions (items, event) {
			// We want to show the actions as part of the tooltip when taping the first time.
			// Copied for handleSelections and modified
			const visualizationGesture = this._getVisualizationGesture();
			const oNewSelection = visualizationGesture.getDataPayload(items, VisTooltipInfo);
			let resolved = visualizationGesture.resolveItems(items);
			if (resolved.length > 0) {
				const _options = items[0].selectionContext ? {
					slots: items[0].selectionContext.slots,
					mapIndex: items[0].selectionContext.mapIndex,
					actions: items[0].selectionContext.actions
				} : {};
				_options.area = items[0].area;
				_options.isAreaSelected = items[0].isAreaSelected;
				let includeApplyFilter, noFilters, noDrillthrough;
				if (items[0].onlyGlobalActions) {
					// Only want to show actions applicable to the full visualization, e.g. TextAction
					includeApplyFilter = false;
					noFilters = true;
					noDrillthrough = true;
				} else {
					includeApplyFilter = true;
					noFilters = false;
					noDrillthrough = false;
				}
				oNewSelection.getCategorySelections().forEach((item) => {
					// this line is needed so that we can add the actions for edge selection in 
					//_getBrushingAndPendingSelections
					item.isEdgeSelection = false;
				});
				visualizationGesture.showSelectionInfo(oNewSelection, event, includeApplyFilter, noFilters, undefined, noDrillthrough, _options);
			} else {
				// show title actions on right-click
				visualizationGesture.titleOrElementStyleActions(event, items);
			}

			return resolved;
		}

		onTap(event){
			if (event.target && event.target.tagName === 'INPUT') {
				// tapping an input in the map search needs to be ignored.
				return;
			}
			const visualizationGesture = this._getVisualizationGesture();
			visualizationGesture.resolveCoordinates(event);

			if (visualizationGesture.isSkipSelection()) {
				visualizationGesture.setEventLocalProperty(event, 'preventDefaultContextBar', true);
			} else {

				const visualizationFeature = this.content.getFeature('Visualization');
				const type = visualizationFeature.getType();

				// For a hierarchy, modify the tap to select right away

				// The grid event handler checks for click type but not tap .. we are trying to trick it.
				if (event.type === 'tap'){
					event.type = 'click';
				}

				var items = visualizationGesture.getTarget() && visualizationGesture.getTarget().getEventTargets ? visualizationGesture.getTarget().getEventTargets(event) : [];
				if (items) {
					if (items.length > 0) {
						const bSelected = visualizationGesture.isItemsSelected(items);
						const bHighlighted = visualizationGesture.isItemsHighlighed(items);

						visualizationGesture.setEventLocalProperty(event, 'preventDefaultContextBar', true);

						//For hierarchy we will select on the first tap
						// when in lass mode, we show the tooltip and actions
						if (type === 'Hierarchy' || ( !visualizationGesture.getLassoController().isLassoMode() &&  (bSelected || bHighlighted)) ){
							// force to toggle / append selections (aka. CTRL + left-click)
							visualizationGesture.handleSelections(event, items, { forceCtrlClick: true });

							if (type !== 'Hierarchy'){
								// show the action associated the selection -- skip the hierarchy because the info is useless
								this.showActions(items, event);
							}

							if (bSelected){
								// clear the highlight if we are deselecting
								visualizationGesture.handleHighlights([]);

							}
						} else {
							const resolved = this.showActions(items, event);
							if (resolved.length > 0) {
								visualizationGesture.handleHighlights(items);
							} else {
								// No datapoint clicked .. clear existing highlights
								visualizationGesture.handleHighlights([]);
							}
						}
					} else {
						visualizationGesture.handleHighlights([]);
						visualizationGesture.clearAll(event);
					}
				}
				// restore the type that we changed to trick the grid event handler 
				if (event.type === 'click'){
					event.type = 'tap';
				}
			}
		}

	}
	return MobileEventMapProvider;
});
